import { useTranslation } from 'react-i18next'
import { Sun, Moon, Monitor } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/renderer/components/ui/select'
import { SegmentedControl } from '@/src/renderer/components/ui/SegmentedControl'
import { useMode, type Mode, type Theme } from '@/src/renderer/hooks/useMode'
import { useLanguage } from '@/src/renderer/contexts/LanguageProvider'
import themesConfig from '@/src/renderer/styles/themes.json'
import { SettingsSection, SettingsRow, SettingsResetCard } from '../../primitives'

/**
 * The "Erscheinungsbild" SubPage under "App & Bedienung". Hosts Mode +
 * Palette + Language using the SettingsSection/Row pattern. Profile moved to
 * "Profil & Daten > Profil" ({@link AccountProfilePage}); Privacy moved to
 * "Profil & Daten > Daten & Privatsphäre" ({@link DataPrivacyPage}); the
 * window-visibility section moved to "App & Bedienung > App-Verhalten"
 * ({@link AppBehaviorPage}).
 */
export function AppearancePage() {
  const { t } = useTranslation()
  const { mode, setMode, theme, setTheme } = useMode()

  // "Default" = the app's cold-start appearance (mode 'light', palette 'standard',
  // matching the ModeProvider fallbacks). The reset stays disabled until the user
  // has actually moved away from it, then reverts both in one click.
  const isAtDefault = mode === 'light' && theme === 'standard'

  const handleReset = () => {
    setMode('light')
    setTheme('standard')
  }

  return (
    <div className="appearance-page">
      <DisplaySection />
      <LanguageSection />
      <SettingsResetCard
        description={t('settings.reset.description')}
        buttonLabel={t('settings.reset.button')}
        onReset={handleReset}
        disabled={isAtDefault}
      />
    </div>
  )
}

// ----- Display (Mode + Palette) ----------------------------------------------

const importedThemes = themesConfig.themes as Array<{ id: string; name: string }>

function DisplaySection() {
  const { t } = useTranslation()
  const { mode, setMode, theme, setTheme } = useMode()

  return (
    <SettingsSection title={t('settings.section.display')} variant="card">
      <SettingsRow
        label={t('preferences.appearance.theme')}
        description={
          mode === 'system'
            ? t('preferences.appearance.systemDescription')
            : t('preferences.appearance.fixedDescription', { theme: t(`preferences.appearance.${mode}`).toLowerCase() })
        }
        control={
          <SegmentedControl<Mode>
            value={mode}
            onValueChange={setMode}
            aria-label={t('preferences.appearance.theme')}
            options={[
              { value: 'light', label: t('preferences.appearance.light'), icon: <Sun size={16} /> },
              { value: 'dark', label: t('preferences.appearance.dark'), icon: <Moon size={16} /> },
              { value: 'system', label: t('preferences.appearance.system'), icon: <Monitor size={16} /> },
            ]}
          />
        }
      />
      <SettingsRow
        label={t('preferences.appearance.palette')}
        description={t('preferences.appearance.paletteDescription')}
        htmlFor="appearance-palette-select"
        control={
          <Select value={theme} onValueChange={(v) => setTheme(v as Theme)}>
            <SelectTrigger id="appearance-palette-select" className="w-[200px]">
              <SelectValue>
                {theme === 'standard'
                  ? t('preferences.appearance.paletteStandard')
                  : importedThemes.find((p) => p.id === theme)?.name ?? t('preferences.appearance.paletteStandard')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* "Standard" is the implicit baseline palette and is NOT in
                  themes.json — render it as a fixed first option, mirroring
                  the pre-redesign AppearanceSection. */}
              <SelectItem value="standard">{t('preferences.appearance.paletteStandard')}</SelectItem>
              {importedThemes.map((palette) => (
                <SelectItem key={palette.id} value={palette.id}>
                  {palette.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </SettingsSection>
  )
}

// ----- Language --------------------------------------------------------------

function LanguageSection() {
  const { t } = useTranslation()
  const { language, changeLanguage } = useLanguage()

  return (
    <SettingsSection title={t('settings.section.language')} variant="card">
      <SettingsRow
        label={t('preferences.language.title')}
        description={t('preferences.language.changesImmediate')}
        control={
          <SegmentedControl
            value={language}
            onValueChange={changeLanguage}
            aria-label={t('language.select')}
            options={[
              { value: 'de', label: t('language.german') },
              { value: 'en', label: t('language.english') },
            ]}
          />
        }
      />
    </SettingsSection>
  )
}
